import React, { useState } from 'react';
import { getWelcomeEmailTemplate } from '@/lib/templates/welcome-email';
import { 
  getPasswordResetEmailTemplate, 
  getEmailVerificationTemplate, 
  getTrialExpirationTemplate,
  getSubscriptionExpiringTemplate,
  getContentGenerationCompleteTemplate,
  getOnboardingDripEmail
} from '@/lib/templates/email-templates';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Footerdemo } from './ui/footer-section';
import { useToast } from '@/components/ui/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { ArrowLeft, Info, ArrowUpDown, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

/**
 * Email Template Preview Component
 * Shows HTML and text versions of an email template
 */
const EmailTemplatePreview = ({ 
  html, 
  text, 
  title, 
  description 
}: { 
  html: string; 
  text: string;
  title: string;
  description?: string;
}) => {
  return (
    <div className="space-y-4">
      <div className="bg-blue-50 p-4 border border-blue-200 rounded-md flex gap-2">
        <Info className="w-5 h-5 text-blue-500 flex-shrink-0" />
        <div>
          <h3 className="font-medium text-blue-800">{title}</h3>
          {description && <p className="text-sm text-blue-700">{description}</p>}
        </div>
      </div>

      <Tabs defaultValue="html">
        <TabsList>
          <TabsTrigger value="html">HTML View</TabsTrigger>
          <TabsTrigger value="text">Text View</TabsTrigger>
          <TabsTrigger value="source">HTML Source</TabsTrigger>
        </TabsList>
        
        <TabsContent value="html" className="mt-4">
          <div className="border rounded-md overflow-hidden">
            <div className="bg-gray-100 p-2 border-b">
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-xs ml-2 text-gray-500">Email Preview</span>
              </div>
            </div>
            <div className="p-6 bg-white">
              <iframe
                srcDoc={html}
                title="Email Template Preview"
                className="w-full h-[500px] bg-white overflow-y-auto"
                sandbox=""
                seamless
              />
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="text" className="mt-4">
          <div className="border rounded-md">
            <div className="bg-gray-100 p-2 border-b">
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-xs ml-2 text-gray-500">Text Fallback</span>
              </div>
            </div>
            <pre className="p-4 whitespace-pre-wrap font-mono text-sm text-gray-700 bg-white overflow-x-auto max-h-[500px] overflow-y-auto">
              {text}
            </pre>
          </div>
        </TabsContent>
        
        <TabsContent value="source" className="mt-4">
          <div className="border rounded-md">
            <div className="bg-gray-100 p-2 border-b">
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-xs ml-2 text-gray-500">HTML Source</span>
              </div>
            </div>
            <pre className="p-4 whitespace-pre-wrap font-mono text-xs text-gray-700 bg-white overflow-x-auto max-h-[500px] overflow-y-auto">
              {html}
            </pre>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

/**
 * Welcome Email Preview Component
 */
const WelcomeEmailPreview = () => {
  const { html, text } = getWelcomeEmailTemplate('John Doe');
  
  return (
    <EmailTemplatePreview
      html={html}
      text={text}
      title="Welcome Email"
      description="Sent to new users when they sign up for an account."
    />
  );
};

/**
 * Password Reset Email Preview Component
 */
const PasswordResetEmailPreview = () => {
  const resetLink = "https://aiseobloggenerator.com/reset-password?token=example-token-12345";
  const { html, text } = getPasswordResetEmailTemplate('Jane Doe', resetLink);

  return (
    <EmailTemplatePreview
      html={html}
      text={text}
      title="Password Reset Email"
      description="Sent when a user requests a password reset."
    />
  );
};

/**
 * Email Verification Preview Component
 */
const EmailVerificationPreview = () => {
  const verificationLink = "https://aiseobloggenerator.com/verify-email?token=example-token-12345";
  const { html, text } = getEmailVerificationTemplate('Alex Johnson', verificationLink);

  return (
    <EmailTemplatePreview
      html={html}
      text={text}
      title="Email Verification"
      description="Sent to verify a user's email address during signup."
    />
  );
};

/**
 * Trial Expiration Warning Preview Component
 */
const TrialExpirationPreview = () => {
  const upgradeLink = "https://aiseobloggenerator.com/upgrade";
  const { html, text } = getTrialExpirationTemplate('Michael Smith', 3, upgradeLink);

  return (
    <EmailTemplatePreview
      html={html}
      text={text}
      title="Trial Expiration Warning"
      description="Sent when a user's free trial is about to end."
    />
  );
};

/**
 * Subscription Expiring Preview Component
 */
const SubscriptionExpiringPreview = () => {
  const renewLink = "https://aiseobloggenerator.com/account/billing";
  const { html, text } = getSubscriptionExpiringTemplate('Sarah Williams', 'July 15, 2024', renewLink);

  return (
    <EmailTemplatePreview
      html={html}
      text={text}
      title="Subscription Expiring Soon"
      description="Sent as a reminder before subscription renewal."
    />
  );
};

/**
 * Content Generation Complete Preview Component
 */
const ContentGenerationCompletePreview = () => {
  const contentLink = "https://aiseobloggenerator.com/posts/abc123";
  const { html, text } = getContentGenerationCompleteTemplate(
    'Chris Taylor', 
    '10 Proven Strategies to Optimize Your Website for Conversions', 
    contentLink,
    3500
  );

  return (
    <EmailTemplatePreview
      html={html}
      text={text}
      title="Content Generation Complete"
      description="Sent when a longer content generation job completes."
    />
  );
};

/**
 * Onboarding Drip Email Preview Component
 */
const OnboardingDripPreview = () => {
  const [templateId, setTemplateId] = useState("strike-list-intro");
  
  // Templates to show in the dropdown
  const templates = [
    { id: "strike-list-intro", name: "Strike List Introduction" },
    { id: "writing-styles-feature", name: "Writing Styles Feature" },
    { id: "marketer-mode-feature", name: "Marketer Mode Feature" },
    { id: "content-frameworks-intro", name: "Content Frameworks Introduction" },
  ];
  
  // Get the selected template
  const { html, text } = getOnboardingDripEmail(templateId, 'Patricia Lee');
  
  // Get the subject from the templates list
  const selectedTemplate = templates.find(t => t.id === templateId);
  
  return (
    <>
      <div className="mb-4 flex items-center gap-2">
        <Zap className="w-5 h-5 text-dogbrown" />
        <h3 className="font-medium">Select Drip Email:</h3>
        <select 
          value={templateId}
          onChange={(e) => setTemplateId(e.target.value)}
          className="border rounded-md px-3 py-1 flex-1"
        >
          {templates.map(template => (
            <option key={template.id} value={template.id}>{template.name}</option>
          ))}
        </select>
      </div>
      
      <EmailTemplatePreview
        html={html}
        text={text}
        title={`Onboarding Drip: ${selectedTemplate?.name || templateId}`}
        description="Part of an onboarding sequence focused on feature education."
      />
    </>
  );
};

/**
 * Main Email Templates Component
 * Displays all available email templates with previews
 */
export default function EmailTemplates() {
  return (
    <div className="min-h-screen pt-16 pb-16 bg-cream">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" asChild className="mr-2">
                <Link to="/">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Link>
              </Button>
              <h1 className="text-3xl font-bold">Email Templates</h1>
            </div>
          </div>
          
          <div className="mt-4 bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-dogblue flex-shrink-0">
                <Info className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2 text-gray-900">Email Templates Preview</h2>
                <p className="text-gray-600">
                  This page displays all email templates used in AISEOBlogGenerator with our updated brand styling. You can preview the HTML and text versions of each template.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="text-xl font-bold mb-4 pb-2 border-b text-dogbrown">Welcome Email</h2>
              <WelcomeEmailPreview />
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="text-xl font-bold mb-4 pb-2 border-b text-dogbrown">Password Reset</h2>
              <PasswordResetEmailPreview />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="text-xl font-bold mb-4 pb-2 border-b text-dogbrown">Email Verification</h2>
              <EmailVerificationPreview />
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="text-xl font-bold mb-4 pb-2 border-b text-dogbrown">Trial Expiration Warning</h2>
              <TrialExpirationPreview />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="text-xl font-bold mb-4 pb-2 border-b text-dogbrown">Subscription Expiring Soon</h2>
              <SubscriptionExpiringPreview />
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="text-xl font-bold mb-4 pb-2 border-b text-dogbrown">Content Generation Complete</h2>
              <ContentGenerationCompletePreview />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="text-xl font-bold mb-4 pb-2 border-b text-dogbrown flex items-center gap-2">
                <ArrowUpDown className="w-5 h-5" />
                Onboarding Drip Emails
              </h2>
              <OnboardingDripPreview />
            </div>
          </div>
        </div>
      </div>
      <Toaster />
      <Footerdemo />
    </div>
  );
}